import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@koob/margaret';
import { KoobBadge, KoobButton, KoobPageHeader } from '@koob/ui';
import { Modal } from 'components';
import ToConnectionRequestStateEnum from 'constants/enums/ToConnectionRequestStateEnum';
import DenyExplainationForm from './DenyExplainationForm';
import { getOrganizationByToDmc } from 'api/node/toConnectionRequest';

const STATE_COLORS = {
  [ToConnectionRequestStateEnum.PENDING]: 'amber',
  [ToConnectionRequestStateEnum.CONFIRMED]: 'green',
  [ToConnectionRequestStateEnum.REFUSED]: 'red',
};

// A TO's connection is one request per product type: each gets a tab,
// greyed out while the TO has not requested that type.
const TYPE_TABS = [
  { type: 'hotel', path: 'hotels' },
  { type: 'experience', path: 'experiences' },
  { type: 'template', path: 'templates' },
];

// The TO's requests of every type ({ id, state }), this one's included.
function useRequestsByType(request) {
  const [requests, setRequests] = useState({});
  const dmcId = request?.dmcOrganization?.id;
  const toId = request?.toOrganization?.id;

  useEffect(() => {
    setRequests({});
    if (!dmcId || !toId) return undefined;
    let cancelled = false;
    Promise.allSettled(
      TYPE_TABS.map(({ type }) =>
        getOrganizationByToDmc({
          dmcOrganizationId: dmcId,
          toOrganizationId: toId,
          kind: type,
        }),
      ),
    ).then((results) => {
      if (cancelled) return;
      setRequests(
        Object.fromEntries(
          results.map((result, index) => [
            TYPE_TABS[index].type,
            result.status === 'fulfilled' ? result.value?.data : undefined,
          ]),
        ),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [dmcId, toId]);

  // This request's own state is fresher than the fetched one (after a save).
  return request?.type
    ? { ...requests, [request.type.toLowerCase()]: request }
    : requests;
}

// The header of one connection request (one product type of a TO): its
// settings and its history, with accept and deny when `save` is given.
// `request` is undefined while it loads.
export default function ConnectionRequestPageHeader({
  request,
  save,
  saving,
  isAllowedToManage,
  loadError,
}) {
  const { t } = useTranslation('toConnectionRequest');
  const [isDenyOpen, setIsDenyOpen] = useState(false);

  const state = request?.state;
  const canDecide = Boolean(save) && isAllowedToManage;
  const canAccept =
    canDecide &&
    state !== ToConnectionRequestStateEnum.CONFIRMED &&
    state !== ToConnectionRequestStateEnum.REFUSED;
  const canDeny = canDecide && state !== ToConnectionRequestStateEnum.REFUSED;

  const requestsByType = useRequestsByType(request);
  const connectionsPath = '/to-connection-request/connections';

  const deny = () => {
    if (window.confirm(t('denyConfirmation'))) {
      setIsDenyOpen(true);
    }
  };

  return (
    <>
      <KoobPageHeader
        title={
          request || loadError
            ? request?.toOrganization?.name || t('pages:connection')
            : undefined
        }
        id={request?.id}
        subtitle={request?.dmcOrganization?.name}
        breadcrumbs={[
          {
            label: t('pages:connections'),
            to: '/to-connection-request/connections',
          },
        ]}
        actions={
          (canDeny || canAccept) && (
            <>
              {canDeny && (
                <KoobButton
                  variant="secondary"
                  onClick={deny}
                  disabled={saving}
                >
                  {t('misc:deny')}
                </KoobButton>
              )}
              {canAccept && (
                <KoobButton
                  variant="primary"
                  onClick={() => save('confirmed', '')}
                  disabled={saving}
                >
                  {t('misc:accept')}
                </KoobButton>
              )}
            </>
          )
        }
        tabs={
          request?.id
            ? [
                ...TYPE_TABS.map(({ type, path }) => {
                  const typeRequest = requestsByType[type];
                  const typeState = typeRequest?.state ?? 'not_requested';
                  return {
                    label: (
                      <span className="flex items-center gap-2">
                        {t(`typeLabels.${type}`)}
                        <KoobBadge
                          color={STATE_COLORS[typeState] ?? 'gray'}
                          size="sm"
                        >
                          {t(`stateLabels.${typeState}`)}
                        </KoobBadge>
                      </span>
                    ),
                    to: `${connectionsPath}/${typeRequest?.id}/${path}`,
                    disabled: !typeRequest?.id,
                  };
                }),
                {
                  label: t('history.tab'),
                  to: `${connectionsPath}/${request.id}/history`,
                },
              ]
            : undefined
        }
      />

      {isDenyOpen && (
        <Modal
          isOpen
          onRequestClose={() => setIsDenyOpen(false)}
          title={
            <Stack size="full" alignY="center" gutterSize={1}>
              <p>{t('denyReason')}</p>
            </Stack>
          }
        >
          <DenyExplainationForm
            onSubmit={(values) => save('refused', values)}
          />
        </Modal>
      )}
    </>
  );
}
